import { useState } from 'react';
import ReactDataSheet from 'react-datasheet';
// Be sure to include styles at some point, probably during your bootstrapping
import 'react-datasheet/lib/react-datasheet.css';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';
import * as Yup from 'yup';

import * as graphql from 'generated/graphql';
import PrimaryButton from 'packages/kakapo-ui-kit/components/buttons/PrimaryButton';
import { PageTitle } from 'packages/kakapo-ui-kit/components/common/Headings';
import { ButtonSize } from 'packages/kakapo-ui-kit/types/Button';
import BulkDateField from 'packages/kakapo-ui-settings-application/pages/bulk-employee-edit/components/BulkDateField';
import BulkField from 'packages/kakapo-ui-settings-application/pages/bulk-employee-edit/components/BulkField';
import BulkSelect, {
	BulkSelectDepartment,
	BulkSelectJobTitle,
	useDepartmentOptions,
	useJobTitleOptions,
} from 'packages/kakapo-ui-settings-application/pages/bulk-employee-edit/components/BulkSelect';
import useGrid from 'packages/kakapo-ui-settings-application/pages/bulk-employee-edit/hooks/useGrid';
import { ISheetEditorEvents } from 'packages/kakapo-ui-settings-application/pages/bulk-employee-edit/types/sheet';

const Indexes = {
	firstName: 0,
	lastName: 1,
	username: 2,
	email: 3,
	activatedAt: 4,
	locale: 5,
	externalReferenceID: 6,
	departmentID: 7,
	jobPositionStartDate: 8,
	jobTitleID: 9,
	readyForUpload: 10,
	status: 11,
};

const validators: Yup.StringSchema[] = [
	Yup.string().min(1).required(),
	Yup.string().min(1).required(),
	Yup.string().min(1).required(),
	Yup.string().email().required(),
	Yup.string(),
	Yup.string().oneOf(['nl_NL', 'en_US']).required(),
	Yup.string(),
	Yup.string().required(),
	Yup.string().when('$jobTitleID', ((jobTitleID: string, schema: Yup.StringSchema) => {
		if (!!jobTitleID) {
			return schema.required();
		}

		return schema;
	}) as any),
	Yup.string(),
];

const validateRow = (row: { value: string }[]) => {
	return validators.forEach((validator, index) => {
		if (index === Indexes.jobPositionStartDate) {
			validator.validateSync(row[index].value, {
				context: { jobTitleID: row[Indexes.jobTitleID].value },
			});
		} else {
			validator.validateSync(row[index].value);
		}
	});
};

const isRowValid = (row: { value: string }[]) => {
	try {
		validateRow(row);
	} catch (err) {
		return false;
	}
	return true;
};

const userFromRow = (row: { value: any }[]): graphql.CreateUserInput => {
	const username = row[Indexes.username].value!.split('@')[0];

	const user: graphql.CreateUserInput = {
		firstName: row[Indexes.firstName].value!,
		lastName: row[Indexes.lastName].value!,
		username,
		email: row[Indexes.email].value!,
		locale: row[Indexes.locale].value!,
		activatedAt: row[Indexes.activatedAt].value,
		externalReferenceID: row[Indexes.externalReferenceID].value,
		groupID: row[Indexes.departmentID].value,
	};

	return user;
};

const renderCellAtIndex = (index: number, value: string, events: ISheetEditorEvents) => {
	if (index === Indexes.locale) {
		return (
			<BulkSelect
				options={[
					{
						label: 'nl_NL',
						value: 'nl_NL',
					},
					{
						label: 'en_US',
						value: 'en_US',
					},
				]}
				value={value}
				events={events}
				validationSchema={validators[index] ?? undefined}
			/>
		);
	}

	if (index === Indexes.activatedAt) {
		return (
			<BulkDateField
				value={value}
				events={events}
				validationSchema={validators[index] ?? undefined}
			/>
		);
	}

	if (index === Indexes.departmentID) {
		return <BulkSelectDepartment value={value} events={events} />;
	}

	if (index === Indexes.jobPositionStartDate) {
		return (
			<BulkDateField
				value={value}
				events={events}
				validationSchema={validators[index] ?? undefined}
			/>
		);
	}

	if (index === Indexes.jobTitleID) {
		return <BulkSelectJobTitle value={value} events={events} />;
	}

	return (
		<BulkField events={events} value={value} validationSchema={validators[index] ?? undefined} />
	);
};

const ScrollElement = styled.div`
	overflow-x: scroll;
`;

const ScrollInnerElement = styled.div`
	width: 2000px;
`;

interface ISheetEditorProps extends ISheetEditorEvents {
	col: number;
	row: number;
	value: any;
}

function BulkEmployeeEdit() {
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [create] = graphql.useCreateUserMutation();
	const [createJobPosition] = graphql.useCreateJobPositionMutation();
	const [grid, setGrid] = useGrid();

	const [, processDepartmentValue] = useDepartmentOptions();
	const [, processJobTitleValue] = useJobTitleOptions();

	const processValueAtIndex = (index: number, value: string): string | undefined => {
		if (index === Indexes.departmentID) {
			return processDepartmentValue(value);
		}

		if (index === Indexes.jobTitleID) {
			return processJobTitleValue(value);
		}

		return value;
	};

	const applyToRow = (row: number, column: number, value: string) => {
		const gridCopy = [...grid];
		gridCopy[row][column] = { ...gridCopy[row][column], value };
		setGrid(gridCopy);
	};

	const applyStatusToRow = (row: number, status: string) => applyToRow(row, Indexes.status, status);

	const startBulkCreate = async () => {
		const successMessage = 'Success';

		setIsLoading(true);

		for (let i = 1; i < grid.length; i += 1) {
			try {
				validateRow(grid[i]);
			} catch (err) {
				applyStatusToRow(i, err.toString());
				// eslint-disable-next-line no-continue
				continue;
			}

			const user = userFromRow(grid[i]);

			try {
				// eslint-disable-next-line no-await-in-loop
				const createResponse = await create({
					variables: {
						input: user!,
					},
				});

				const createdUser = createResponse.data?.createUser.user;

				if (createdUser && grid[i][Indexes.jobTitleID].value) {
					await createJobPosition({
						variables: {
							input: {
								jobTitleID: grid[i][Indexes.jobTitleID].value,
								startDate: grid[i][Indexes.jobPositionStartDate].value,
								userID: createdUser.id,
							},
						},
					});
				}

				applyStatusToRow(i, successMessage);
			} catch (err) {
				applyStatusToRow(i, 'Server error.');
			}
		}

		// Remove created users from array.
		setGrid(
			grid.filter((row: { value: string }[]) => row[Indexes.status].value !== successMessage),
		);

		setIsLoading(false);
	};

	return (
		<Box p={3}>
			<Flex mb={4} justifyContent="space-between">
				<PageTitle>Bulk user upload</PageTitle>
				<PrimaryButton
					size={ButtonSize.Small}
					disabled={isLoading}
					onClick={() => startBulkCreate()}
				>
					Save all changes
				</PrimaryButton>
			</Flex>

			<ScrollElement>
				<ScrollInnerElement>
					<ReactDataSheet
						disablePageClick
						data={grid}
						valueRenderer={(cell: any) => cell.value}
						onCellsChanged={(changes) => {
							const updatedGrid = grid.map((row: any) => [...row]);

							// Apply changes to existing cells
							changes.forEach(({ cell, row, col, value }) => {
								updatedGrid[row][col] = {
									...updatedGrid[row][col],
									value: processValueAtIndex(col, value!),
								};
							});

							const changedRows = Array.from(new Set(changes.map((change) => change.row)));
							changedRows.forEach((row) => {
								updatedGrid[row][Indexes.readyForUpload] = {
									value: isRowValid(updatedGrid[row]) ? 'Ja' : 'Nee',
									readOnly: true,
								};
							});

							setGrid(updatedGrid);
						}}
						dataEditor={({ col, value, row, onChange, onRevert, onCommit }: ISheetEditorProps) => {
							return renderCellAtIndex(col, value, { onChange, onRevert, onCommit });
						}}
					/>
				</ScrollInnerElement>
			</ScrollElement>
		</Box>
	);
}

export default BulkEmployeeEdit;
